import Logo from "../../../assets/icons/logo.svg";
import DarkLogo from "../../../assets/lightIcons/logo_light.svg";
import ArrowBack from "../../../assets/icons/arrow_back.svg";
import ArrowBackLight from "../../../assets/lightIcons/arrow_back_light.svg";
import { format, parseISO, parse, isValid } from "date-fns";
import Test from "../../../assets/images/test.png";
import MapPin from "../../../assets/icons/map-pin.svg";
import { joinEvent } from "../../../redux/slices/eventSlice";
import { setModalModal } from "../../../redux/slices/modalSlice";
import { postRequestUnAuthenticated } from "../../../services/rest";
import GoogleMapsEvent from "./GoogleMapsEvent";
import {
  FiChevronLeft,
  FiCalendar,
  FiClock,
  FiMapPin,
  FiVideo,
  FiShare2,
  FiGlobe,
  FiLock,
  FiCheck,
} from "react-icons/fi";
import {
  React,
  useState,
  useEffect,
  useRef,
  useNavigate,
  useDispatch,
  useSearchParams,
  useSelector,
  useParams,
  useModal,
  useLocation,
} from "../../../globals/imports";
import { getAppPrefix } from "../../EmbedChannels/utility/embedHelper";

const EventFullPage = () => {
  const dispatch = useDispatch();
  const myData = useSelector((state) => state.myData);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [event, setEvent] = useState({});
  const [loading, setLoading] = useState(false);
  const [formattedStartDate, setFormattedStartDate] = useState("");
  const [formattedEndDate, setFormattedEndDate] = useState("");
  const [formattedStartTime, setFormattedStartTime] = useState("");
  const [formattedEndTime, setFormattedEndTime] = useState("");
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const fullPath = location.pathname;
  const { eventId } = useParams();
  const { handleOpenModal } = useModal();

  useEffect(() => {
    const generateEventData = async () => {
      setLoading(true);
      const response = await postRequestUnAuthenticated(`/fetch/event/data`, {
        eventId: eventId,
      });
      setLoading(false);
      if (response.success) {
        setEvent(response.event);
      }
    };
    generateEventData();
  }, []);

  const [joining, setJoining] = useState(false);
  const handleJoinEvent = () => {
    // In the embed the website auth flag isn't set; a populated myData._id is
    // the reliable "authenticated" signal across the website and embed stores.
    if (isLoggedIn || myData?._id) {
      setJoining(true);
      dispatch(joinEvent(event._id))
        .unwrap()
        .then((updated) => {
          // Reflect the new membership immediately.
          if (updated && updated._id) {
            setEvent((prev) => ({ ...prev, ...updated }));
          } else if (event.joining === "public") {
            setEvent((prev) => ({
              ...prev,
              joined_users: [...(prev.joined_users || []), myData._id],
            }));
          } else {
            setEvent((prev) => ({
              ...prev,
              requested_users: [...(prev.requested_users || []), myData._id],
            }));
          }
        })
        .catch(() => {
          console.error("Issue in joining event. Please try again.");
        })
        .finally(() => setJoining(false));
    } else {
      navigate(`${getAppPrefix()}/get-started?redirect=${fullPath}`);
    }
  };

  useEffect(() => {
    const handleClickOutsideDropdown = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false); // Close dropdown
      } else {
      }
    };
    document.addEventListener("mousedown", handleClickOutsideDropdown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideDropdown);
    };
  }, [isDropdownOpen]);

  const formatDateTime = (startDate, endDate, startTime, endTime) => {
    try {
      const parsedStartDate = parseISO(startDate);
      const parsedEndDate = endDate ? parseISO(endDate) : null;

      if (!isValid(parsedStartDate)) throw new Error("Invalid start date");
      if (endDate && !isValid(parsedEndDate))
        throw new Error("Invalid end date");

      const startDateFormatted = format(parsedStartDate, "yyyy-MM-dd");
      const endDateFormatted = parsedEndDate
        ? format(parsedEndDate, "yyyy-MM-dd")
        : null;

      const parsedStartTime = startTime
        ? parse(startTime, "dd/M/yyyy, hh:mm:ss a", new Date())
        : null;
      const parsedEndTime = endTime
        ? parse(endTime, "dd/M/yyyy, hh:mm:ss a", new Date())
        : null;

      const startTimeFormatted =
        parsedStartTime && isValid(parsedStartTime)
          ? format(parsedStartTime, "HH:mm")
          : "";
      const endTimeFormatted =
        parsedEndTime && isValid(parsedEndTime)
          ? format(parsedEndTime, "HH:mm")
          : "";

      setFormattedStartDate(startDateFormatted);
      setFormattedEndDate(endDateFormatted || startDateFormatted);
      setFormattedStartTime(startTimeFormatted);
      setFormattedEndTime(endTimeFormatted);

      const displayStartDate = format(parsedStartDate, "dd MMM");
      const displayEndDate = parsedEndDate
        ? format(parsedEndDate, "dd MMM")
        : null;

      const displayStartTime =
        parsedStartTime && isValid(parsedStartTime)
          ? format(parsedStartTime, "hh:mm a")
          : "";
      const displayEndTime =
        parsedEndTime && isValid(parsedEndTime)
          ? format(parsedEndTime, "hh:mm a")
          : "";

      setDate(
        `${displayStartDate}${displayEndDate ? ` - ${displayEndDate}` : ""}`
      );
      setTime(
        `${displayStartTime}${displayEndTime ? ` - ${displayEndTime}` : ""}`
      );
    } catch (error) {
      console.error("Error formatting date/time:", error.message);
    }
  };

  const handleGoogleCalendar = (e) => {
    e.stopPropagation();
    try {
      const startUTC = new Date(
        `${formattedStartDate}T${formattedStartTime}`
      ).toISOString();
      const endUTC = new Date(
        `${formattedEndDate}T${formattedEndTime}`
      ).toISOString();

      const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
        event.name
      )}&dates=${startUTC.replace(/[-:]/g, "").slice(0, -5)}Z/${endUTC
        .replace(/[-:]/g, "")
        .slice(0, -5)}Z&details=${encodeURIComponent(
        event.description || ""
      )}&location=${encodeURIComponent(event.location || "Online")}&ctz=${
        event.timezone
      }`;

      window.open(googleCalendarUrl, "_blank");
    } catch (error) {
      console.error("Error generating Google Calendar link:", error.message);
    }
  };

  const handleShareEvent = (id) => {
    dispatch(setModalModal({ field: "eventId", value: event._id }));
    handleOpenModal("modalShareEventOpen");
  };

  const handleOutlookCalendar = (e) => {
    e.stopPropagation();
    try {
      const startUTC = new Date(
        `${formattedStartDate}T${formattedStartTime}`
      ).toISOString();
      const endUTC = new Date(
        `${formattedEndDate}T${formattedEndTime}`
      ).toISOString();

      const outlookUrl = `https://outlook.live.com/calendar/0/deeplink/compose?path=/calendar/action/compose&subject=${encodeURIComponent(
        event.name
      )}&startdt=${startUTC}&enddt=${endUTC}&body=${encodeURIComponent(
        event.description || ""
      )}&location=${encodeURIComponent(event.location || "Online")}&timezone=${
        event.timezone || "UTC"
      }`;

      window.open(outlookUrl, "_blank");
    } catch (error) {
      console.error("Error generating Outlook Calendar link:", error.message);
    }
  };

  const handleDownloadICS = (e) => {
    e.stopPropagation();
    try {
      const startUTC =
        new Date(`${formattedStartDate}T${formattedStartTime}`)
          .toISOString()
          .replace(/[-:]/g, "")
          .slice(0, -5) + "Z";
      const endUTC =
        new Date(`${formattedEndDate}T${formattedEndTime}`)
          .toISOString()
          .replace(/[-:]/g, "")
          .slice(0, -5) + "Z";

      const icsContent = `
            BEGIN:VCALENDAR
            VERSION:2.0
            CALSCALE:GREGORIAN
            BEGIN:VEVENT
            SUMMARY:${event.name}
            DESCRIPTION:${event.description || ""}
            LOCATION:${event.location || "Online"}
            DTSTART:${startUTC}
            DTEND:${endUTC}
            END:VEVENT
            END:VCALENDAR
                `.trim();

      const blob = new Blob([icsContent], {
        type: "text/calendar;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${event.name || "Event"}.ics`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error generating ICS file:", error.message);
    }
  };

  const handleLocation = (url) => {
    if (url) {
      window.open(url, "_blank");
    } else {
      console.error("Location URL is not provided");
    }
  };

  useEffect(() => {
    if (event.name) {
      formatDateTime(
        event.startDate,
        event.endDate,
        event.startTime,
        event.endTime
      );
    }
  }, [
    event.startDate,
    event.endDate,
    event.startTime,
    event.endTime,
    event.name,
  ]);

  const isHost = event.user === myData?._id;
  const isJoined = event?.joined_users?.includes(myData?._id) || isHost;
  const isRequested = event?.requested_users?.includes(myData?._id);
  const isPublic = event.joining === "public";
  const hasPlace = !!(event.location && event.locationText);
  const isOnline = !!event.meet_url && !hasPlace;

  const MetaRow = ({ icon, children }) => (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-theme-primaryBackground text-theme-secondaryText">
        {icon}
      </span>
      <div className="min-w-0 flex-1 pt-1 text-sm text-theme-secondaryText">
        {children}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-theme-primaryBackground">
      <div className="mx-auto w-full max-w-2xl px-4 py-4">
        <button
          onClick={() => navigate(-1)}
          className="mb-4 inline-flex items-center gap-1 text-sm font-light text-theme-secondaryText transition-opacity hover:opacity-70"
        >
          <FiChevronLeft size={18} /> Back
        </button>

        {loading && !event.name ? (
          <div className="overflow-hidden rounded-2xl border border-theme-chatDivider bg-theme-tertiaryBackground">
            <div className="h-44 w-full animate-pulse bg-theme-secondaryBackground" />
            <div className="space-y-3 p-5">
              <div className="h-6 w-2/3 animate-pulse rounded bg-theme-secondaryBackground" />
              <div className="h-4 w-1/2 animate-pulse rounded bg-theme-secondaryBackground" />
            </div>
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-theme-chatDivider bg-theme-tertiaryBackground">
            {/* Hero */}
            <div className="relative h-44 w-full bg-theme-secondaryBackground sm:h-52">
              {event.cover_image ? (
                <img
                  src={event.cover_image}
                  alt={event.name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-theme-secondaryBackground to-theme-primaryBackground">
                  <FiCalendar size={40} className="text-theme-placeholder" />
                </div>
              )}
              {date && (
                <div className="absolute left-4 top-4 flex flex-col items-center rounded-lg bg-theme-tertiaryBackground/95 px-3 py-1.5 text-center shadow-sm backdrop-blur">
                  <span className="text-base font-semibold leading-none text-theme-secondaryText">
                    {date.split(" ")[0]}
                  </span>
                  <span className="mt-0.5 text-[10px] font-medium uppercase tracking-wide text-theme-placeholder">
                    {date.split(" ")[1]}
                  </span>
                </div>
              )}
            </div>

            {/* Body */}
            <div className="space-y-5 p-5">
              <div className="space-y-2">
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ${
                    isPublic
                      ? "bg-theme-primaryBackground text-theme-secondaryText"
                      : "bg-theme-primaryBackground text-theme-emptyEvent"
                  }`}
                >
                  {isPublic ? <FiGlobe size={12} /> : <FiLock size={12} />}
                  {isPublic ? "Public event" : "Private event"}
                </span>
                <h1 className="text-xl font-semibold leading-snug text-theme-secondaryText sm:text-2xl">
                  {event.name}
                </h1>
              </div>

              {/* Meta */}
              <div className="space-y-3">
                {date && (
                  <MetaRow icon={<FiCalendar size={15} />}>
                    <span className="font-medium">{date}</span>
                  </MetaRow>
                )}
                {time && (
                  <MetaRow icon={<FiClock size={15} />}>
                    <span>{time}</span>
                    {event.timezone && (
                      <span className="text-theme-placeholder">
                        {" "}
                        · {event.timezone}
                      </span>
                    )}
                  </MetaRow>
                )}
                {isOnline ? (
                  <MetaRow icon={<FiVideo size={15} />}>
                    <span className="font-medium">Online event</span>
                    {event.meet_url && (
                      <a
                        href={event.meet_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-0.5 block truncate text-xs text-theme-sidebarColor underline"
                      >
                        {event.meet_url}
                      </a>
                    )}
                  </MetaRow>
                ) : (
                  hasPlace && (
                    <MetaRow icon={<FiMapPin size={15} />}>
                      <button
                        onClick={() => handleLocation(event.location)}
                        className="text-left hover:underline"
                      >
                        {event.locationText}
                      </button>
                    </MetaRow>
                  )
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2.5">
                {isJoined ? (
                  <div className="relative flex-1">
                    <button
                      className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-theme-secondaryText px-4 py-2.5 text-sm font-medium text-theme-primaryBackground transition-opacity hover:opacity-90"
                      onClick={() => setIsDropdownOpen(true)}
                    >
                      <FiCalendar size={15} /> Add to Calendar
                    </button>
                    {isDropdownOpen && (
                      <div
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div
                          className="w-64 overflow-hidden rounded-xl border border-theme-chatDivider bg-theme-tertiaryBackground shadow-xl"
                          ref={dropdownRef}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <p className="border-b border-theme-chatDivider px-4 py-3 text-xs font-semibold uppercase tracking-wide text-theme-placeholder">
                            Add to calendar
                          </p>
                          {[
                            ["Google", handleGoogleCalendar],
                            ["Apple / ICS", handleDownloadICS],
                            ["Outlook", handleOutlookCalendar],
                          ].map(([label, fn]) => (
                            <div
                              key={label}
                              className="cursor-pointer px-4 py-3 text-sm font-light text-theme-secondaryText transition-colors hover:bg-theme-primaryBackground"
                              onClick={fn}
                            >
                              {label}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : isRequested ? (
                  <div className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-theme-primaryBackground px-4 py-2.5 text-sm font-medium text-theme-emptyEvent">
                    <FiCheck size={15} /> Requested
                  </div>
                ) : (
                  <button
                    className="flex-1 rounded-lg bg-theme-secondaryText px-4 py-2.5 text-sm font-medium text-theme-primaryBackground transition-opacity hover:opacity-90 disabled:opacity-50"
                    onClick={handleJoinEvent}
                    disabled={joining}
                  >
                    {joining
                      ? "Please wait…"
                      : isPublic
                      ? "Join event"
                      : "Request access"}
                  </button>
                )}
                <button
                  className="inline-flex items-center gap-1.5 rounded-lg border border-theme-chatDivider px-4 py-2.5 text-sm font-light text-theme-secondaryText transition-colors hover:bg-theme-primaryBackground"
                  onClick={handleShareEvent}
                >
                  <FiShare2 size={14} /> Share
                </button>
              </div>

              <div className="h-px w-full bg-theme-chatDivider" />

              {/* About */}
              <div>
                <h3 className="mb-1.5 text-sm font-semibold text-theme-secondaryText">
                  About this event
                </h3>
                <p className="whitespace-pre-wrap break-words text-sm font-light leading-relaxed text-theme-secondaryText">
                  {event.description || (
                    <span className="text-theme-placeholder">
                      No description provided.
                    </span>
                  )}
                </p>
              </div>

              {/* Location / map */}
              {hasPlace && (
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-theme-secondaryText">
                    Location
                  </h3>
                  <GoogleMapsEvent
                    url={event.location}
                    text={event.locationText}
                  />
                  <button
                    onClick={() => handleLocation(event.location)}
                    className="mt-2 flex items-start gap-2 text-left text-sm font-light text-theme-secondaryText hover:underline"
                  >
                    <FiMapPin
                      size={14}
                      className="mt-0.5 flex-shrink-0 text-theme-placeholder"
                    />
                    {event.locationText}
                  </button>
                </div>
              )}

              {/* Join-type note */}
              <p className="text-xs font-light italic text-theme-emptyEvent">
                {isPublic
                  ? "This is a public event — anyone can join."
                  : "This is a private event, so entry requires approval. You'll receive an email once the admin reviews your request."}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventFullPage;
